import { toast } from "sonner"
import { MessageChatCircle, Phone } from "@untitledui/icons"

import { DesignDialog } from "@/components/ui/design-dialog"
import { useUpdateEstimateStatus } from "@/lib/api/use-estimates"
import { tasksApi } from "@/lib/api/tasks"
import { cx } from "@/lib/utils/cx"

// Moving an estimate to In Progress is the one act in this product that
// reaches a real customer: it releases the drip and texts them their booking
// link. So it is deliberately NOT a silent drag consequence — it is one tap
// that names what will happen, with a way to say "just move it, I'll call".
//
// The dev's fear on the call was the shop dragging a card back and forth and
// re-texting the customer each time. That can't happen: the enrolment anchor
// is written once and only a return to Draft clears it, so re-entry resumes
// mid-sequence. This dialog says so out loud when the texts already went.

interface StartFollowUpDialogProps {
  open: boolean
  onClose: () => void
  estimateId: number
  customerName: string
  hasPhone: boolean
  /** Set once the first automated message has gone out. */
  alreadyContactedAt?: string | null
  onStarted?: () => void
}

export function StartFollowUpDialog({
  open,
  onClose,
  estimateId,
  customerName,
  hasPhone,
  alreadyContactedAt,
  onStarted,
}: StartFollowUpDialogProps) {
  const update = useUpdateEstimateStatus()
  const resuming = !!alreadyContactedAt

  const move = async (enrol: boolean) => {
    // Order matters: the opt-out flag has to land BEFORE the status change,
    // because the move into InProgress is what triggers enrolment.
    if (!enrol) {
      try {
        await tasksApi.setOutreach(estimateId, true)
      } catch {
        toast.error("Couldn't hold the automatic texts — nothing was moved.")
        return
      }
    }
    update.mutate(
      { estimateId, status: "InProgress" },
      {
        onSuccess: () => {
          toast.success(
            resuming
              ? `Back in follow-up — ${customerName} keeps the messages they already had`
              : enrol && hasPhone
                ? `Follow-up started — ${customerName} gets a text with the booking link`
                : enrol
                  ? "Moved. Add a phone number to start text follow-up."
                  : `Moved — nothing sent. ${customerName} is yours to call.`,
            {
              // Undo the outward-facing act while it still can be undone.
              action: {
                label: "Undo",
                onClick: () =>
                  update.mutate(
                    { estimateId, status: "Confirmed" },
                    {
                      onSuccess: () =>
                        toast("Back to Confirmed — anything already sent stays sent"),
                    },
                  ),
              },
            },
          )
          onStarted?.()
          onClose()
        },
        onError: () => toast.error("Couldn't move that estimate."),
      },
    )
  }

  return (
    <DesignDialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <DesignDialog.Content width="sm">
        <DesignDialog.Header>
          <DesignDialog.Title>
            {resuming ? `Put ${customerName} back in follow-up?` : `Start follow-up with ${customerName}?`}
          </DesignDialog.Title>
          <DesignDialog.Description>
            {resuming ? (
              <>
                Already texted{" "}
                {new Date(alreadyContactedAt!).toLocaleDateString("en-US", {
                  weekday: "long",
                })}{" "}
                — moving back won't re-send anything.
              </>
            ) : hasPhone ? (
              "We'll text them the estimate and their booking link."
            ) : (
              "There's no phone number on file, so nothing will be texted yet."
            )}
          </DesignDialog.Description>
        </DesignDialog.Header>

        <DesignDialog.Body>
          <div className="space-y-2">
            <button
              type="button"
              disabled={update.isPending}
              onClick={() => move(true)}
              className={cx(
                "w-full min-h-[52px] rounded-xl px-4 py-3 text-left text-white transition-colors duration-150 disabled:opacity-50",
                "bg-teal-600 hover:bg-teal-700",
              )}
            >
              <span className="flex items-center gap-2 text-[13.5px] font-semibold">
                <MessageChatCircle className="size-4" aria-hidden />
                {resuming ? "Resume follow-up" : "Start follow-up"}
              </span>
              <span className="block text-[11.5px] text-white/80 mt-0.5">
                {resuming
                  ? "Picks up where the sequence left off"
                  : "Automatic texts and their booking link"}
              </span>
            </button>

            {!resuming && (
              <button
                type="button"
                disabled={update.isPending}
                onClick={() => move(false)}
                className="w-full min-h-[52px] rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 px-4 py-3 text-left transition-colors duration-150 disabled:opacity-50"
              >
                <span className="flex items-center gap-2 text-[13.5px] font-semibold text-zinc-900">
                  <Phone className="size-4 text-zinc-400" aria-hidden />
                  Just move it — I'll call them myself
                </span>
                <span className="block text-[11.5px] text-zinc-500 mt-0.5">
                  Nothing is sent to the customer
                </span>
              </button>
            )}
          </div>
        </DesignDialog.Body>

        <DesignDialog.Footer>
          <span />
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-3.5 rounded-xl text-[13px] font-semibold text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            Cancel
          </button>
        </DesignDialog.Footer>
      </DesignDialog.Content>
    </DesignDialog.Root>
  )
}
